import { avatarInitials, avatarSlot, type AvatarSlot } from '@overlayqa/shared';
import { cn } from '@/lib/utils';

interface AvatarProps {
  /** Image URL. When absent/empty, falls back to the initial of `name`. */
  src?: string | null;
  /** Used for the initial fallback (and img alt). */
  name?: string | null;
  /**
   * STABLE identity key for the person - their user id, never a display name.
   * Decides the fallback's colour so one person is the same colour here, in the
   * extension sidebar and in team review. Optional: an unknown author still
   * gets a stable colour rather than an unstyled blob.
   */
  id?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const DIMS: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'w-5 h-5 text-[10px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
};

/**
 * Slot -> design-system classes.
 *
 * Written out in full ON PURPOSE. Tailwind's JIT scans source for literal class
 * strings, so a computed `bg-avatar-${slot}-light` is invisible to it and those
 * utilities would simply never be generated - the avatar would render with no
 * background at all, and only in a production build.
 */
const SLOT_CLASSES: Record<AvatarSlot, string> = {
  1: 'bg-avatar-1-light text-avatar-1',
  2: 'bg-avatar-2-light text-avatar-2',
  3: 'bg-avatar-3-light text-avatar-3',
  4: 'bg-avatar-4-light text-avatar-4',
  5: 'bg-avatar-5-light text-avatar-5',
  6: 'bg-avatar-6-light text-avatar-6',
};

/**
 * Design-system avatar. Single source for user/person avatars - renders the
 * image when present, else the person's initial on their own colour. Change it
 * here to update every avatar in the app + the design-system gallery.
 *
 * The palette lives in tailwind.config.js (`avatar.N`), mirrored from
 * AVATAR_PALETTE in @overlayqa/shared; `pnpm avatar-palette:check` fails if the
 * two drift.
 */
export function Avatar({ src, name, id, size = 'md', className }: AvatarProps) {
  const dim = DIMS[size];
  if (src) {
    return <img src={src} alt="" className={cn(dim, 'rounded-full border border-gray-200 object-cover shrink-0', className)} />;
  }
  const initial = avatarInitials(name);
  // Fall back to the name only when no id is supplied - better a stable colour
  // keyed on something than an uncoloured avatar. Call sites should pass `id`.
  const slot = avatarSlot(id ?? name);
  return (
    <span className={cn(dim, 'rounded-full flex items-center justify-center font-medium shrink-0', SLOT_CLASSES[slot], className)}>
      {initial}
    </span>
  );
}
